import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import { HTMLDescription } from '../../HTMLDescription'
import { getFeedTypeForEntitySeries } from '../../../modules/entities/types'

import {
  ListGridButtonGroup,
  useViewTypeFromSessionStorage,
} from '../../../routes/groups/components/entity/learning-series-list'
import ContentFeed, { defaultWideLayout } from '../../../ui/Feeds'
import Breadcrumbs from './Breadcrumbs'
import SeriesRow from './rows/SeriesRow'
import { ViewType } from './rows/types'
import { useSeriesCourseText } from '@/utils/series-course-text'

export const wrapWithLayout = (children): React.ReactElement => {
  return <div className="learning-cards">{children}</div>
}

export const wrapWithTable = (children): React.ReactElement => {
  return (
    <table>
      <thead />
      <tbody>{children}</tbody>
    </table>
  )
}

interface Props {
  series: any
  languages: string[]
  onItemClick: (series) => void
  heading?: React.ReactElement
  showDetails?: boolean
  introHTML?: string
  showBreadcrumbs?: boolean
  showGroupFeatured?: boolean
  resourceType?: string
  resourceID?: string | number
}

const SeriesList = ({
  series,
  languages,
  onItemClick,
  heading,
  showDetails,
  introHTML,
  showBreadcrumbs = false,
  showGroupFeatured,
  resourceType,
  resourceID,
}: Props): React.ReactElement => {
  const { t } = useTranslation('learning')

  const { usingCourse } = useSeriesCourseText()

  const [lang, setLang] = useState(
    languages.length > 1
      ? languages.find(x => x === 'English')
        ? 'English'
        : languages[0]
      : undefined
  )
  const [searchInput, setSearchInput] = useState('')
  const [viewType, setViewType] = useViewTypeFromSessionStorage()

  if (!series && !showGroupFeatured)
    return (
      <div>
        {usingCourse
          ? t('learning.library_no_courses_to_show')
          : t('learning.library_no_series_to_show')}
      </div>
    )

  const seriesRows = series
    .filter(x => !lang || x.language === lang)
    .map((singleSeries, ix) => (
      <SeriesRow
        ix={ix}
        key={singleSeries.id}
        series={singleSeries}
        viewType={viewType}
        onViewModules={() => onItemClick(singleSeries)}
      />
    ))
    .filter(row =>
      row.props.series.title?.toLowerCase().includes(searchInput.toLowerCase())
    )

  if (!heading) {
    heading = usingCourse
      ? t('group:group.learning_library_title_courses', {
          count: seriesRows.length,
        })
      : t('group:group.learning_library_title_series', {
          count: seriesRows.length,
        })
  }

  return (
    <div
      style={{ padding: `${showBreadcrumbs ? '0' : '20'}px 10px 20px` }}
      className="learning-series-list"
    >
      {showBreadcrumbs ? <Breadcrumbs /> : null}
      {showDetails && introHTML ? (
        <div
          style={{
            borderBottom: 'solid 1px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px',
          }}
        >
          <HTMLDescription
            description={introHTML}
            className="learning-item__description"
          />
        </div>
      ) : null}
      {showGroupFeatured ? (
        <div
          style={{
            marginBottom: '2rem',
          }}
        >
          <ContentFeed
            t={t}
            feed_type={getFeedTypeForEntitySeries(resourceType)}
            feed_id={resourceID}
            count={12}
            paginate
            continuous
            layout={defaultWideLayout}
            cardProps={{
              resourceContext: {
                type: resourceType,
                id: resourceID,
              },
            }}
            titleID={'feed:feed_titles.group_featured_learning'}
          />
        </div>
      ) : null}
      {seriesRows.length ? (
        <>
          <div className="learning-list__controls">
            <div>
              <h1>{heading}</h1>
            </div>
            {languages.length > 1 ? (
              <div className="learning-list__controls__language">
                <div className="fs-unmask search-field__select-wrapper">
                  <select
                    className="fs-unmask search-field__input search-field__select learning-list__search-field"
                    style={{ width: '15em', height: 38 }}
                    onChange={e => setLang(e.target.value)}
                    defaultValue={languages[0]}
                  >
                    {languages.map(x => (
                      <option value={x} key={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}
            <div className="learning-list__controls__input-and-btn-group-wrapper">
              <input
                placeholder={usingCourse ? 'Search course…' : 'Search series…'}
                value={searchInput}
                style={{ marginRight: 5 }}
                className="fs-unmask search-field__input learning-list__search-field"
                onChange={e => setSearchInput(e.target.value)}
              />
              <ListGridButtonGroup
                viewType={viewType}
                setViewType={setViewType}
              />
            </div>
          </div>
          {seriesRows.length > 0 && viewType === ViewType.table
            ? wrapWithTable(seriesRows)
            : wrapWithLayout(seriesRows)}
          {seriesRows.length === 0 && (
            <div>
              {usingCourse
                ? t('learning.library_no_courses_to_show')
                : t('learning.library_no_series_to_show')}
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

export default SeriesList
